<template>
  <div class="relative flex justify-center w-full mb-2">
    <img src="@/assets/logo.png" alt="logo" class="logoIlly">
    <div class="absolute right-0 top-0">
      <span 
        @click="switchLanguage" 
        class="cursor-pointer text-white font-bold text-[14px] sm:text-[34px] hover:opacity-80 transition-opacity"
      >
         {{ languageLabel }}
      </span>
    </div>
  </div>
  <div>
    <p class="text-center text-title font-bold title-section">
      {{ $t('formPage.title') }}
    </p>

    <form @submit.prevent="handleSubmit">
      <div class="input-group-spacing">
        <label class="block text-label font-semibold mb-2">{{ $t('formPage.fnameTitle') }}</label>
        <input
            type="text"
            :value="userInfo.fname"
            :placeholder="$t('formPage.fnamePlaceholder')"
            @input="updateFName($event.target.value)"
            class="w-full text-input font-semibold input-padding rounded-full bg-[#ffffff4d] placeholder-[#e38b8b] text-white border border-transparent focus:border-white focus:ring-0 outline-none"
            required
        />
      </div>

      <div class="input-group-spacing">
        <label class="block text-label mb-2">{{ $t('formPage.lnameTitle') }}</label>
        <input
            type="text"
            :value="userInfo.lname"
            :placeholder="$t('formPage.lnamePlaceholder')"
            @input="updateLName($event.target.value)"
            class="w-full text-input font-semibold input-padding rounded-full bg-[#ffffff4d] placeholder-[#e38b8b] text-white border border-transparent focus:border-white focus:ring-0 outline-none"
            required
        />
      </div>

      <div class="checkbox-group-spacing">
        <label class="block text-label mb-2">{{ $t('formPage.emailTitle') }}</label>
        <input
            type="email"
            :value="userInfo.email"
            :placeholder="$t('formPage.emailPlaceholder')"
            @input="updateEmail($event.target.value)"
            class="w-full text-input font-semibold input-padding rounded-full bg-[#ffffff4d] placeholder-[#e38b8b] text-white border border-transparent focus:border-white focus:ring-0 outline-none"
            required
        />
      </div>

      <div class="checkbox-container flex items-center text-input font-semibold checkbox-group-spacing">
        <input
            type="checkbox"
            id="acceptGdpr"
            required
            @input="updateAcceptGdpr($event.target.checked)"
        >
        <label for="acceptGdpr">
          <a target="_blank"
             :href="gdprLink"
             class="underline">
            {{ $t('formPage.gdprPlaceholder') }}
          </a>
        </label>
      </div>

      <div class="checkbox-container flex items-center text-input font-semibold checkbox-group-spacing">
        <input
            type="checkbox"
            id="newsletter"
            @input="updateNewsletter($event.target.checked)"
        >
        <label for="newsletter">
          {{ $t('formPage.newsletterPlaceholder') }}
        </label>
      </div>
      <div class="text-error italic font-semibold error-msg-spacing">
        <p v-if="!isFormValid"> {{ $t('formPage.requiredFields') }}</p>
      </div>

      <button
          :disabled="!isFormValid"
          type="submit"
          class="w-full flex items-center justify-between submit-btn-padding rounded-full border border-white text-button font-semibold hover:text-[#e18b8b] transition"
      >
        <span>{{ $t('formPage.submitButton') }}</span>
        <svg id="Group_8" class="responsive-svg" data-name="Group 8" xmlns="http://www.w3.org/2000/svg"
             xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 10.016 17.369">
          <defs>
            <clipPath id="clip-path">
              <rect id="Rectangle_20" data-name="Rectangle 20" width="10.016" height="17.369" fill="#fff"/>
            </clipPath>
          </defs>
          <g id="Group_7" data-name="Group 7" clip-path="url(#clip-path)">
            <path id="Path_5" data-name="Path 5"
                  d="M10.016,8.671,1.615.27l0,0h0A.949.949,0,0,0,.269,1.608h0L7.344,8.683.271,15.757h0A.949.949,0,0,0,1.611,17.1h0l8.4-8.4L10,8.683Z"
                  transform="translate(0 0)" fill="#fff"/>
          </g>
        </svg>
      </button>
    </form>
  </div>

  <div v-if="toastMessage" class="fixed bottom-5 left-1/2 transform -translate-x-1/2">
    <div id="toast-warning"
         class="flex items-center p-4 w-auto max-w-full text-gray-500 bg-white rounded-lg shadow-lg"
         role="alert">
      <div
          class="inline-flex items-center justify-center flex-shrink-0 w-[3vw] h-[3vw] text-yellow-500 bg-yellow-100 rounded-lg">
        <svg class="w-[2.5vw] h-[2.5vw]" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor"
             viewBox="0 0 20 20">
          <path
              d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-4a1 1 0 0 1-2 0V6a1 1 0 0 1 2 0v5Z"/>
        </svg>
        <span class="sr-only">Warning icon</span>
      </div>
      <div class="ml-3 text-toast font-light whitespace-nowrap">{{ toastMessage }}</div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    userInfo: Object
  },
  data() {
    return {
      showImage: false,
      submitted: false,
      toastMessage: ''
    };
  },
  mounted() {
    this.showImage = true;
  },
  computed: {
    isFormValid() {
      return this.userInfo.fname.trim() !== '' &&
          this.userInfo.lname.trim() !== '' &&
          this.userInfo.email.trim() !== '' &&
          this.userInfo.acceptGdpr;
    },
    languageLabel() {
      return this.$route.path === '/en' ? 'EL' : 'EN';
    },
    gdprLink() {
      return this.$route.path === '/en'
          ? 'https://kafeaterra.gr/en/fineprint/privacy-policy/'
          : 'https://kafeaterra.gr/%ce%bf%cf%81%ce%bf%ce%b9-%ce%ba%ce%b1%ce%b9-%cf%80%cf%81%ce%bf%cf%8b%cf%80%ce%bf%ce%b8%ce%b5%cf%83%ce%b5%ce%b9%cf%82/%cf%80%ce%bf%ce%bb%ce%b9%cf%84%ce%b9%ce%ba%ce%b7-%ce%b4%ce%b5%ce%b4%ce%bf%ce%bc%ce%b5%ce%bd%cf%89%ce%bd/';
    }
  },
  methods: {
    switchLanguage() {
      const targetPath = this.$route.path === '/en' ? '/' : '/en';
      this.$router.push(targetPath);
    },
    updateFName(value) {
      this.$emit('update-user-info', {...this.userInfo, fname: value});
    },
    updateLName(value) {
      this.$emit('update-user-info', {...this.userInfo, lname: value});
    },
    updateEmail(value) {
      this.$emit('update-user-info', {...this.userInfo, email: value});
    },
    updateNewsletter(value) {
      const isAcceptedNewsletter = value === true;
      this.$emit('update-user-info', {...this.userInfo, newsletter: isAcceptedNewsletter});
    },
    updateAcceptGdpr(value) {
      const isAccepted = value === true;
      this.$emit('update-user-info', {...this.userInfo, acceptGdpr: isAccepted});
    },
    showToast(message) {
      this.toastMessage = message;
      setTimeout(() => {
        this.toastMessage = '';
      }, 3000);
    },
    async handleSubmit() {
      let dataForCollectionData = {
        firstName: this.userInfo.fname,
        lastName: this.userInfo.lname,
        email: this.userInfo.email,
        language: this.$route.path === '/en' ? 'en' : 'el',
        acceptGdpr: this.userInfo.acceptGdpr,
        acceptNewsletter: this.userInfo.newsletter
      };

      let requestOptions = {
        method: 'POST',
        body: JSON.stringify(dataForCollectionData),
        headers: {'Content-Type': 'application/json'}
      };

      fetch("https://questions-with-rewards-api.ocm.workers.dev/api/register", requestOptions)
          .then(async (response) => {
            const data = await response.json();
            console.log(response.status)
            if (this.isFormValid && (response.status !== 409 && response.status !== 404 && response.status !== 500)) {
              this.submitted = true;
              console.log('hashedEmail ', data)
              this.$emit('submit-user-info', data.email);
            } else if (response.status === 409) {
              this.showToast(this.$t('formPage.toastMsg'));
            }
          })
          .catch(error => console.error('Error upload user:', error));
    },
  }
};
</script>

<style>
.logoIlly {
  width: 35%;
  box-shadow: inset 0px 0 20px 20px rgb(43 43 43 / 11%), 0px 3px 20px 0 rgb(43 43 43 / 11%);
}

.el-scrollbar__thumb {
  background-color: #EC0000 !important;
  opacity: 1 !important;
}

.el-scrollbar__bar.is-vertical {
  display: flex !important;
}

.el-scrollbar {
  margin: 8px !important;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 2vw;
}

.checkbox-container input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  width: 21px;
  height: 21px;
  background-color: white;
  border-radius: 50%;
  position: relative;
  cursor: pointer;
}

.checkbox-container input[type="checkbox"]:checked::before {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  width: 13px;
  height: 13px;
  background-color: #cb0000;
  border-radius: 50%;
}

.checkbox-container label {
  cursor: pointer;
}

.responsive-svg {
  width: 10px;
  height: 17px;
}

.text-title { font-size: 18px; }
.text-label { font-size: 15px; }
.text-input { font-size: 16px; }
.text-error { font-size: 14px; }
.text-button { font-size: 16px; }
.text-toast { font-size: 4vw; }

.title-section { width: 88vw; margin-top: 40px; margin-bottom: 30px; }
.input-group-spacing { padding-bottom: 18px; }
.input-padding { padding: 8px 12px; }
.checkbox-group-spacing { padding-bottom: 22px; }
.newsletter-group-spacing { padding-bottom: 36px; }
.error-msg-spacing { padding-top:10px;padding-bottom: 12px; }
.submit-btn-padding { padding: 12px 17px; }

@media (min-width: 900px) {
  .logoIlly { width: 40%;}
  .text-title { font-size: 50px; }
  .text-label { font-size: 44px; }
  .text-input { font-size: 40px; }
  .text-error { font-size: 38px; }
  .text-button { font-size: 44px; }

  .title-section { margin-top: 65px; margin-bottom: 35px; }
  .input-group-spacing { padding-bottom: 40px; }
  .input-padding { padding: 20px 25px; }
  .checkbox-group-spacing { padding-bottom: 40px; }
  .newsletter-group-spacing { padding-bottom: 30px; }
  .error-msg-spacing { padding-top: 20px;padding-bottom: 35px; }
  .submit-btn-padding { padding: 20px 40px; }
  .checkbox-container input[type="checkbox"] { width: 25px; height: 25px; }
  .checkbox-container input[type="checkbox"]:checked::before { width: 17px; height: 17px;}
  .responsive-svg { width: 18px; height: 30px;}
}
</style>